import {assert} from 'Asserts';
import {SampleDetails} from 'constants/SampleConstants';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Cell} from 'notebook/tables/constants/CellConstants';
import Excess from 'notebook/tables/excess';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {BatchTypedValue, BioentityTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import {Optional} from 'util/Types';
import * as StageRunUtil from 'workflows/util/StageRunUtil';
import {StageRun} from 'workflows/WorkflowConstants';

function getSourceSampleCellValue(sampleDetails: SampleDetails): Cell {
  let typedValue: BioentityTypedValue | BatchTypedValue | null;
  if (sampleDetails.bioentityInfo) {
    typedValue = {
      type: BaseTypedValueType.BIOENTITY,
      bioentitySummary: sampleDetails.bioentityInfo,
    };
  } else if (sampleDetails.batchInfo) {
    typedValue = {
      type: BaseTypedValueType.BATCH,
      batchInfo: sampleDetails.batchInfo,
    };
  } else {
    return {text: ''};
  }

  return {typedValue, text: ''};
}

/**
 * Takes a cell which may contain relative formulas and anchors it to
 * a particular column and position.  This function assumes that the
 * initial row that the formula was in is row 0.
 *
 * @param cell - a potentially partial Cell which may or may not have a text key
 * @param origI - the row number that the cell was in
 * @param origJ - the column number that the cell was in
 * @param newI - the row number that the cell is in now
 * @param newJ - the column number that the cell is in now
 */
export function anchorFormulas({
  cell,
  origI,
  origJ,
  newI,
  newJ,
}: {
  cell: Partial<Cell>;
  origI: number;
  origJ: number;
  newI: number;
  newJ: number;
}): Partial<Cell> {
  if (cell.text) {
    const origCoordinates = {row: origI, column: origJ};
    const newCoordinates = {row: newI, column: newJ};
    const text = Excess.tokenizer.toAbsoluteFormula(
      Excess.tokenizer.toRelativeFormula(cell.text, origCoordinates),
      newCoordinates
    );
    return {
      ...cell,
      text,
    };
  } else {
    return cell;
  }
}

export function getSourceColumnCellFromSampleDetails(
  columnDefinition: ColumnDefinition,
  sampleDetails: SampleDetails
): Cell {
  assert(
    columnDefinition.columnGroup === TransferTableColumnGroup.SOURCE,
    'Tried to get source sample cell value for non-source column'
  );
  const locked = TransferTableUtil.getLockedCellProperty(columnDefinition);
  switch (columnDefinition.type) {
    case TableColumnType.SAMPLE: {
      return {
        ...locked,
        ...getSourceSampleCellValue(sampleDetails),
      };
    }
    case TableColumnType.CONTAINERS: {
      const cell = {...locked, text: ''};
      if (sampleDetails.containerInfo) {
        const {id, name, barcode} = sampleDetails.containerInfo;
        cell.typedValue = {
          type: BaseTypedValueType.STORABLES,
          storableSummaries: [{id, name, barcode}],
        };
      }
      return cell;
    }
    case TableColumnType.PLATE: {
      const cell = {...locked, text: ''};
      if (sampleDetails.plateInfo) {
        const {id, name, barcode} = sampleDetails.plateInfo;
        cell.typedValue = {
          type: BaseTypedValueType.STORABLES,
          storableSummaries: [{id, name, barcode}],
        };
      }
      return cell;
    }
    case TableColumnType.POSITION: {
      const cell = {...locked, text: ''};
      if (sampleDetails.plateInfo && sampleDetails.containerInfo && sampleDetails.containerInfo.gridCell) {
        cell.typedValue = {
          type: BaseTypedValueType.GRID_COORDINATES,
          gridCoordinates: sampleDetails.containerInfo.gridCell,
        };
      }
      return cell;
    }
    default:
      throw new Error(`Unexpected column type ${columnDefinition.type}`);
  }
}

export function getDefaultCell(
  columnDefinition: ColumnDefinition,
  stageRun: Optional<StageRun>,
  sampleDetails: SampleDetails,
  structuredTableColumn: StructuredTableColumn,
  i: number,
  j: number
): Cell {
  const locked = TransferTableUtil.getLockedCellProperty(columnDefinition);
  const defaultCell = {
    ...locked,
    text: '',
    ...anchorFormulas({
      cell: structuredTableColumn.defaultValue || {},
      origI: 0,
      origJ: j,
      newI: i,
      newJ: j,
    }),
  };

  if (columnDefinition.columnGroup === TransferTableColumnGroup.SOURCE) {
    return getSourceColumnCellFromSampleDetails(columnDefinition, sampleDetails);
  }
  switch (columnDefinition.type) {
    case TableColumnType.STAGE_RUN:
      assert(stageRun != null, 'Stage run must exist for a stage run column');
      return {
        ...locked,
        text: '',
        typedValue: stageRun ? StageRunUtil.stageRunTypedValue(stageRun) : undefined,
      };
    case TableColumnType.NUMBER_OF_CONTAINERS:
      if (defaultCell.text === '' && defaultCell.typedValue == null) {
        return {...defaultCell, text: '1'};
      } else {
        return defaultCell;
      }
    default:
      return defaultCell;
  }
}

export enum SourceSampleType {
  NO_CONTAINER = 'NO_CONTAINER',
  PLATE_CONTAINER = 'PLATE_CONTAINER',
  NON_PLATE_CONTAINER = 'NON_PLATE_CONTAINER',
}

export function getSourceSampleType(sampleDetails: SampleDetails): SourceSampleType {
  if (sampleDetails.containerInfo == null) {
    return SourceSampleType.NO_CONTAINER;
  }
  assert(
    sampleDetails.plateInfo == null ||
      (sampleDetails.containerInfo != null && sampleDetails.containerInfo.gridCell != null),
    `Expected gridCell to exist if the sample is on a plate`,
    {
      sampleDetails,
    }
  );
  return sampleDetails.plateInfo == null
    ? SourceSampleType.NON_PLATE_CONTAINER
    : SourceSampleType.PLATE_CONTAINER;
}

export function getSourceSampleContainerStructuredTableColumns(
  sourceSampleTypes: Set<SourceSampleType>
): Array<StructuredTableColumn> {
  const columns: Array<StructuredTableColumn> = [];

  if (sourceSampleTypes.has(SourceSampleType.NON_PLATE_CONTAINER)) {
    columns.push(
      makeStructuredTableColumn({
        columnType: TableColumnType.CONTAINERS,
        name: 'Source Container',
        columnGroup: TransferTableColumnGroup.SOURCE,
      })
    );
  }

  if (sourceSampleTypes.has(SourceSampleType.PLATE_CONTAINER)) {
    columns.push(
      makeStructuredTableColumn({
        columnType: TableColumnType.PLATE,
        name: 'Source Plate',
        columnGroup: TransferTableColumnGroup.SOURCE,
      })
    );
    columns.push(
      makeStructuredTableColumn({
        columnType: TableColumnType.POSITION,
        name: 'Source Position',
        columnGroup: TransferTableColumnGroup.SOURCE,
      })
    );
  }

  return columns;
}

/**
 * Return the set of source columns the Transfer Table should have based on its sample types.
 */
export function getSourceSampleStructuredTableColumns(
  sourceSampleTypes: Set<SourceSampleType>
): Array<StructuredTableColumn> {
  const sampleColumn = makeStructuredTableColumn({
    columnType: TableColumnType.SAMPLE,
    name: 'Sample',
    columnGroup: TransferTableColumnGroup.SOURCE,
  });
  const containerColumns = getSourceSampleContainerStructuredTableColumns(sourceSampleTypes);
  return [sampleColumn, ...containerColumns];
}

export function getSupportedSampleTypesFromColumns(
  columns: Array<StructuredTableColumn>
): Set<SourceSampleType> {
  assert(columns.length > 0, 'At least 1 column is expected in Transfer Tables');
  assert(
    columns[0].columnType === TableColumnType.SAMPLE &&
      columns[0].columnGroup === TransferTableColumnGroup.SOURCE,
    'First column in the Transfer Table is expected to be the source sample.'
  );

  const supportedSampleTypes = new Set([SourceSampleType.NO_CONTAINER]);
  for (const column of columns) {
    if (column.columnGroup !== TransferTableColumnGroup.SOURCE) {
      continue;
    }
    if (column.columnType === TableColumnType.CONTAINERS) {
      supportedSampleTypes.add(SourceSampleType.NON_PLATE_CONTAINER);
    }
    if (column.columnType === TableColumnType.PLATE) {
      assert(
        columns.some(
          (otherColumn) =>
            otherColumn.columnType === TableColumnType.POSITION &&
            otherColumn.columnGroup === TransferTableColumnGroup.SOURCE
        ),
        'Expected table with Source Plate column to also have a Source Position column'
      );
      supportedSampleTypes.add(SourceSampleType.PLATE_CONTAINER);
    }
  }
  return supportedSampleTypes;
}
